/**
 * @file applicant.tsx
 *
 * @fileoverview This HOC will deny an applicant to access a specific page.
 *  If the user is an applicant, it will make an appropriate redirect.
 */
import { GetServerSidePropsContext } from 'next';
import { WithAuthenticateProps } from '~/jobiqo/hocs/withAuthenticate';
import { getUser, isApplicant } from '~/jobiqo/lib/auth';

interface WithoutAuthenticateApplicantProps extends WithAuthenticateProps {
  /**
   * A custom place to redirect to if user is an applicant.
   *
   * @default "/403"
   */
  redirectTo?: string;
}

/**
 * Higher order getServerSideProps function that can be called from inside a page.
 *
 * @param {GetServerSideProps} gssp
 *  The getServerSideProps props function from the page that calls this function.
 * @param {string} redirectTo
 *  The redirect route.
 * @returns
 *  Called gssp function or appropriate getServerSideProps return value.
 */
export const withoutAuthenticateApplicant = ({
  gssp,
  redirectTo = '/403',
}: WithoutAuthenticateApplicantProps) => {
  return async (ctx: GetServerSidePropsContext) => {
    const user = await getUser(ctx);
    const userIsApplicant = await isApplicant(user);

    if (userIsApplicant) {
      // If the user is an applicant, redirect to 403.
      return {
        redirect: {
          destination: redirectTo,
          permanent: false,
        },
        props: {},
      };
    }

    if (gssp) {
      return await gssp(ctx);
    }

    return {
      props: {},
    };
  };
};
